import React from "react";
import {
  Box,
  Text,
  Flex,
  Divider,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  HStack,
} from "@chakra-ui/react";
import { BiChevronDown } from "react-icons/bi";
import GarrazCopyText from "./GaraazCopyText";
import { GrDownload } from "react-icons/gr";

// Define types for items, subgroups, and groups
type Item = {
  key: string;
  value: string | number;
  onClick?: () => void;
  copyable?: boolean;
  downloadable?: boolean;
  onDownload?: () => void;
  isKeyVisible?: boolean;
  itemStyle?: React.CSSProperties;
};

type Subgroup = {
  onDownload?: any;
  downloadable?: any;
  copyable?: boolean;
  title?: string;
  items: Item[];
  subgroupTitleStyle?: React.CSSProperties;
};

type Group = {
  title?: string;
  copyable?: boolean;
  subgroups: Subgroup[];
  groupTitleStyle?: React.CSSProperties;
};

type InfoPopoverProps = {
  triggerLabel: string;
  groups: Group[];
  triggerStyle?: React.CSSProperties;
  triggerProps?: React.HTMLAttributes<HTMLElement>;
};

const GaraazInfoPopover: React.FC<InfoPopoverProps> = ({
  triggerLabel,
  groups,
  triggerStyle,
  triggerProps,
}) => {
  return (
    <Popover trigger="hover" placement="bottom-start">
      <PopoverTrigger>
        <Text
          fontWeight={"bold"}
          textTransform={"uppercase"}
          cursor={"pointer"}
          fontSize={"xs"}
          display={"flex"}
          alignItems={"center"}
          style={{ ...triggerStyle }}
          {...triggerProps}
        >
          {triggerLabel}

          <BiChevronDown style={{ display: "inline", fontSize: "1.5em" }} />
        </Text>
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverBody>
          {groups.map((group, groupIndex) => (
            <Box key={groupIndex} mb={4}>
              {/* Group Title */}
              <HStack alignItems={"center"}>
                <Text
                  color={"#4A5567"}
                  fontSize={"lg"}
                  fontWeight={"bold"}
                  textTransform={"capitalize"}
                  style={{ ...group.groupTitleStyle }}
                >
                  {group.title}
                </Text>
                {group.copyable && (
                  <GarrazCopyText
                    text={
                      group?.subgroups
                        .map((e: any) =>
                          e?.items
                            ?.map((e: any) => e?.value?.toString())
                            .join(`\n`)
                        )
                        .join(`\n`) as any
                    }
                    popOverMessage={`Details Copied`}
                  ></GarrazCopyText>
                )}
              </HStack>
              {/* Subgroups */}
              {group.subgroups.map((subgroup, subgroupIndex) => (
                <Box key={subgroupIndex} mb={4} mt={2}>
                  {/* SubGroup Title */}
                  <Text
                    color={"#4A5567"}
                    fontSize={"md"}
                    fontWeight={"bold"}
                    textTransform={"capitalize"}
                    style={{ ...subgroup.subgroupTitleStyle }}
                  >
                    <Box display={"flex"} alignItems={"center"}>
                      <Text fontSize={"md"} ml={2}>
                        {" "}
                        {subgroup.title}
                      </Text>

                      {subgroup.downloadable && subgroup.onDownload && (
                        <IconButton
                          icon={<GrDownload />}
                          onClick={subgroup.onDownload}
                          aria-label="download"
                          size={"xs"}
                          bg={"none"}
                          ml={"auto"}
                        />
                      )}
                      {subgroup.copyable && (
                        <GarrazCopyText
                          text={
                            subgroup?.items
                              ?.map((item) => {
                                return item.isKeyVisible
                                  ? `${item.key} : ${item.value}`
                                  : item.value;
                              })
                              .join("\n") as any
                          }
                          popOverMessage={`${subgroup?.title} Copied`}
                        ></GarrazCopyText>
                      )}
                    </Box>
                  </Text>

                  {/* Items */}
                  <Box mt={2}>
                    {subgroup.items.map((item, itemIndex) => (
                      <Flex
                        key={itemIndex}
                        align="center"
                        ml={5}
                        style={{ ...item.itemStyle }}
                      >
                        {item.isKeyVisible && (
                          <Text
                            fontSize={"sm"}
                            minWidth={"90px"}
                            onClick={item.onClick ? item.onClick : undefined}
                          >
                            {item.key} :{" "}
                          </Text>
                        )}

                        <Text
                          fontSize={"sm"}
                          onClick={item.onClick ? item.onClick : undefined}
                        >
                          {item.value}
                        </Text>

                        {item.copyable && (
                          <GarrazCopyText
                            text={item.value as any}
                            popOverMessage={`${item.key || "Text"} Copied`}
                          ></GarrazCopyText>
                        )}
                      </Flex>
                    ))}
                  </Box>
                  {subgroupIndex < group.subgroups.length - 1 && (
                    <Divider mt={2} />
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default GaraazInfoPopover;
